import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

// Define schemas for each location type
const stateSchema = z.object({
  name: z.string().min(2),
  gstCode: z.string().length(2),
  capitalCityId: z.number().optional(),
});

const districtSchema = z.object({
  name: z.string().min(2),
  code: z.string().length(4),
  stateId: z.number(),
  capitalCityId: z.number().optional(),
});

const mandalSchema = z.object({
  name: z.string().min(2),
  code: z.string().length(6),
  districtId: z.number(),
});

const citySchema = z.object({
  name: z.string().min(2),
  code: z.string().length(4),
  districtId: z.number(),
  mandalId: z.number().optional(),
});

// Create type for the schema union
const schemas = {
  states: stateSchema,
  districts: districtSchema,
  mandals: mandalSchema,
  cities: citySchema,
} as const;

type LocationType = keyof typeof schemas;
type LocationData<T extends LocationType> = z.infer<typeof schemas[T]>;

interface LocationFormProps<T extends LocationType> {
  type: T;
  data?: LocationData<T>;
  onSubmit: (data: LocationData<T>) => void;
  onCancel: () => void;
}

export const LocationForm = <T extends LocationType>({ 
  type, 
  data, 
  onSubmit, 
  onCancel 
}: LocationFormProps<T>) => {
  const schema = schemas[type];

  const form = useForm<LocationData<T>>({
    resolver: zodResolver(schema),
    defaultValues: data || {} as LocationData<T>,
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {type === 'states' && (
          <FormField
            control={form.control}
            name="gstCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>GST Code</FormLabel>
                <FormControl>
                  <Input {...field} maxLength={2} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Add conditional form fields for other types */}

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Form>
  );
}; 